import { Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import { TradableDetailUiModel } from "@modules/staking/domain/entities/tradable-detail-ui.model";
import { StakingBannerUiModel } from "@modules/staking/domain/entities/staking-banner-ui.model";
import { GetStakeableDataByIdQuery } from "../../../../api/graphql/staking/generated/staking.stakeable-info-by-id.service.graphql-gen";
import { StakingAdpRemote } from "@modules/staking/domain/infrastructure/staking-adp.remote";
import { StakingWidgetStateService } from "@modules/staking/domain/+state/staking-widget.state.service";
import { StakingHelperService } from "@modules/staking/domain/+state/utility/staking-helper.service";
import { StakingWidgetHandlerModel } from "@modules/staking/domain/entities/staking-widget-handler.model";

@Injectable({
	providedIn: "root"
})
export class StakedAssetFacade {
	public constructor(
		private readonly stakingAdpRemote: StakingAdpRemote,
		private readonly stakingWidgetStateService: StakingWidgetStateService,
		private readonly stakingHelperService: StakingHelperService
	) {}

	public getAdpStakeable$(): Observable<Stakeable> {
		return this.stakingWidgetStateService.getStakeable$();
	}

	public updateStakeable(stakeable: Stakeable): void {
		this.stakingWidgetStateService.updateWidgetState({ stakeable: stakeable });
	}

	public removeStakeable(): void {
		this.stakingWidgetStateService.updateWidgetState({ stakeable: null });
	}

	public fetchAndUpdateStakable(assetId: string): Observable<Stakeable> {
		return this.stakingAdpRemote.fetchStakeableInfo(assetId).pipe(
			take(1),
			map((stakeableInfo: GetStakeableDataByIdQuery) => {
				const isStakeable: boolean = stakeableInfo.assetConfig.assets.length > 0;
				const stakeable: Stakeable = isStakeable
					? this.stakingHelperService.processStakeableInfo(stakeableInfo)
					: null;
				this.updateStakeable(stakeable);
				return stakeable;
			})
		);
	}

	public isStakedAsset$(): Observable<boolean> {
		return this.getAdpStakeable$().pipe(map((stakeable: Stakeable) => +stakeable?.stakedAmount > 0));
	}

	public isAssetSoldOut$(): Observable<boolean> {
		return this.getAdpStakeable$().pipe(
			map((stakeable: Stakeable) => stakeable?.availability === StakeableAvailability.SOLD_OUT)
		);
	}

	public isAssetEnabled$(): Observable<boolean> {
		return this.getAdpStakeable$().pipe(
			map((stakeable: Stakeable) => stakeable?.availability === StakeableAvailability.ENABLED)
		);
	}

	private getProfitById$(): Observable<number> {
		return this.getAdpStakeable$().pipe(map((stakeable) => +stakeable?.yearlyInterest));
	}

	public getTradableDetailData$(): Observable<TradableDetailUiModel> {
		return combineLatest([this.isAssetSoldOut$(), this.isStakedAsset$(), this.isAssetEnabled$()]).pipe(
			map(([isSoldOut, isStaked, isEnabled]) => ({ isSoldOut, isStaked, isEnabled }))
		);
	}

	public getBannerData$(): Observable<StakingBannerUiModel> {
		return combineLatest([this.getProfitById$(), this.getAdpStakeable$()]).pipe(
			map(([profit, stakeable]) => ({ profit, stakeable }))
		);
	}

	public getStakingWidgetUiState$(): Observable<StakingWidgetHandlerModel> {
		return combineLatest([this.getTradableDetailData$(), this.getAdpStakeable$()]).pipe(
			map(([tradableUiModel, stakeable]) => ({
				widgetAvailability: tradableUiModel,
				stakeable
			}))
		);
	}
}
